import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Divider,
} from '@mui/material';
import {
    Tv as TvIcon,
    CalendarToday as CalendarIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';

const MotDePasseProject: React.FC = () => {
    return (
        <Box>
            {/* Header avec titre et image */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <img
                    src="/TV/MDP.png"
                    alt="Logo Mot de Passe"
                    style={{
                        width: '64px',
                        height: '64px',
                        objectFit: 'contain',
                        filter: 'drop-shadow(2px 2px 4px rgba(167, 139, 250, 0.3))',
                    }}
                />
                <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold'
                    }}
                >
                    Mot de Passe
                </Typography>
            </Box>

            {/* Statut et date */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                        2020
                    </Typography>
                </Box>
            </Box>

            {/* Description */}
            <Typography
                variant="body1"
                sx={{ mb: 3, lineHeight: 1.6 }}
            >
                Développement du système de jeu pour le retour de l'émission culte "Mot de Passe"
                après plusieurs années d'arrêt. Contribution au développement de l'habillage graphique
                et modernisation des mécaniques de jeu tout en préservant l'esprit authentique
                du format original.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Missions réalisées */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold'
                    }}
                >
                    Missions réalisées
                </Typography>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2
                }}>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Développement système de jeu complet
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Logique des mécaniques de jeu
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Gestion des scores et compteurs
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Interface de contrôle régie
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Participation habillage graphique
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Modernisation du format
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Tests et validation plateau
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Support technique émission
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Technologies (générales) */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold'
                    }}
                >
                    Domaines techniques
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['Systèmes de jeu', 'Logique métier', 'Habillage graphique', 'Intégration plateau', 'Modernisation format', 'Support production'].map((tech) => (
                        <Chip
                            key={tech}
                            label={tech}
                            variant="filled"
                            size="small"
                            sx={{
                                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(20, 184, 166, 0.1) 100%)',
                                color: '#a78bfa',
                                border: '1px solid rgba(167, 139, 250, 0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2) 0%, rgba(20, 184, 166, 0.2) 100%)',
                                },
                            }}
                        />
                    ))}
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Défis techniques */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold'
                    }}
                >
                    Défis techniques relevés
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Redémarrage après interruption :</strong> Recréer entièrement un système
                    de jeu moderne pour une émission arrêtée depuis plusieurs années, en analysant
                    les mécaniques originales et les standards actuels.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Préservation de l'authenticité :</strong> Moderniser les outils techniques
                    tout en conservant l'esprit et les mécaniques qui ont fait le succès du format
                    historique "Mot de Passe".
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Intégration habillage/logique :</strong> Synchroniser parfaitement
                    le système de jeu avec l'habillage graphique pour une expérience télévisuelle
                    fluide et moderne.
                </Typography>
                <Typography variant="body2">
                    <strong>Fiabilité production :</strong> Assurer un fonctionnement sans faille
                    pour le retour d'une émission emblématique avec des attentes élevées du public
                    et des équipes de production.
                </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Contexte historique */}
            <Box sx={{
                p: 2,
                backgroundColor: 'rgba(167, 139, 250, 0.05)',
                borderRadius: 2,
                border: '1px solid rgba(167, 139, 250, 0.1)'
            }}>
                <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    textAlign: 'center'
                }}>
                    <RefreshIcon sx={{ fontSize: '1rem', verticalAlign: 'middle', mr: 1 }} />
                    Contribution au retour d'une émission culte : développement complet du système
                    pour sa dernière édition
                </Typography>
            </Box>
        </Box>
    );
};

export default MotDePasseProject;
